import React from 'react'
import SkeletonLoader from '../skeleton-loader'
import {
  Button,
  ParagraphText,
  Checkbox,
  Icon,
  Tooltip,
} from '@thryvlabs/maverick'
import {usePaymentCheckboxes} from './contexts/payment-checkboxes-context'
import {usePaymentAddress} from './contexts/payment-address-context'

import {MainContainer} from '../ui/payment-information'
import {thryvSalesLogo} from '../../assets'
import {PaymentForm} from './recurly-payment-form'
import {PaymentMethod} from './payment-method'
import {AchPaymentForm} from './ach-payment-form'
import {BecsPaymentForm} from './becs-payment-form'
import {InvoicePaymentForm} from './invoice-payment-form'
import {FutureInvoicesNotice} from './future-invoices-notice'
import {formatPrice} from '../../utils/currency-format'
import {BillingNoticeModal} from '../../components/billing-notice-modal'
import {toast} from 'react-toastify'
import ExpiryCountdownTimer from '../expiry-countdown-timer'

const DropdownProducts = ({title, addonsList}) => {
  const [toggleDropdown, setToggleDropdown] = React.useState(false)
  const handleToggleDropdown = () => {
    setToggleDropdown(!toggleDropdown)
  }

  const setItems = () => {
    return addonsList.map(({name, price}, index) => (
      <div className="d-flex justify-content-between" key={index}>
        <ParagraphText variant="sm">{name}</ParagraphText>
        <ParagraphText variant="sm">{price}</ParagraphText>
      </div>
    ))
  }

  return (
    <div className="dropdown">
      <button
        className="btn dropdown-toggle"
        type="button"
        id="dropdownMenuButton"
        data-toggle="dropdown"
        onClick={handleToggleDropdown}
        style={{
          outline: 'none',
          boxShadow: 'none',
          fontSize: '20px',
          fontFamily: 'Open Sans,sans-serif',
        }}
        onBlur={() => setToggleDropdown(false)}
      >
        {title}
      </button>
      <div
        className={`dropdown-menu p-4 ${toggleDropdown ? 'show' : ''}`}
        aria-labelledby="dropdownMenuButton"
        style={{minWidth: '350px'}}
      >
        {setItems()}
      </div>
    </div>
  )
}

const SEO_GST_CODES = [
  'addon-4-seobase-m2m',
  'addon-4-seopb-m2m',
  'addon-4-seokey-m2m',
]

const TotalOrSemiAnnualDue = ({paymentState}) => {
  const {monthlyAddons, semiAnnuallyAddons, oneTimeAddons, paymentInfo} =
    paymentState
  const isAus = paymentInfo?.country === 'AU'
  const hasDesignFeeWaive = paymentInfo.coupons?.some(
    coupon => coupon.code === 'urldbdesignfee',
  )
  const isWebsiteSetupFeeLine = ({addonPlanCode, name}) =>
    hasDesignFeeWaive &&
    name?.includes('(Setup Fee)') &&
    addonPlanCode?.startsWith('addon-4-website-')
  const getAddonsList = () => [
    {
      name: paymentInfo.plan.planName,
      price: formatPrice(paymentInfo.plan.currency.amount),
    },
    ...[...oneTimeAddons, ...monthlyAddons, ...semiAnnuallyAddons]
      .filter(addon => {
        const {addonPlanCode, isDomainValid} = addon
        if (addonPlanCode === paymentInfo.plan.planCode) {
          return false
        }
        if (addonPlanCode === 'ent-4-website-free' && isDomainValid) {
          return false
        }
        if (isWebsiteSetupFeeLine(addon)) {
          return false
        }
        return addonPlanCode !== 'onboarding-fee'
      })
      .map(
        ({
          name,
          code,
          currency,
          quantity,
          pricingModel,
          rampIntervals = [],
        }) => {
          const actualCurrency =
            pricingModel === 'ramp'
              ? rampIntervals?.[1]?.currencies?.find(
                  ({currency: _currency}) => _currency === currency?.currency,
                )
              : currency
          const unitAmount =
            isAus && SEO_GST_CODES.includes(code)
              ? (actualCurrency?.unitAmount ?? 0) * 1.1
              : actualCurrency?.unitAmount
          return {
            name: name,
            price: formatPrice(unitAmount, quantity),
          }
        },
      ),
    ...paymentInfo.coupons
      .filter(
        coupon =>
          !(
            coupon.duration === 'temporal' &&
            coupon.durationUnit === 'month' &&
            coupon.durationAmount === 1
          ) && !(hasDesignFeeWaive && coupon.code === 'urldbdesignfee'),
      )
      .map(coupon => {
        let couponDiscountAmount = 0

        const selectedPlanCodes = [
          ...monthlyAddons,
          ...oneTimeAddons,
          {
            code: paymentInfo.plan.planCode,
            name: paymentInfo.plan.name,
            currency: {unitAmount: paymentInfo.plan.currency.amount},
            quantity: 1,
          },
        ]

        if (coupon.discountAmount) {
          couponDiscountAmount = coupon.discountAmount
        } else if (coupon.isPercentDiscount && coupon.appliesToAllPlans) {
          selectedPlanCodes.forEach(addon => {
            if (!addon.code.includes('Setup Fee')) {
              couponDiscountAmount +=
                addon.currency.unitAmount *
                addon.quantity *
                (coupon.discountPercent / 100)
            }
          })
        } else {
          selectedPlanCodes.forEach(addon => {
            if (
              coupon?.appliesToPlans?.includes(addon.code) &&
              !addon.name.includes('Setup Fee')
            ) {
              couponDiscountAmount +=
                addon.currency.unitAmount *
                addon.quantity *
                (coupon.discountPercent / 100)
            }
          })
        }
        return {
          name: coupon.description || coupon.name,
          price: parseFloat(-couponDiscountAmount).toFixed(2),
        }
      })
      .filter(x => !isNaN(x.price)),
  ]

  const addonsList = getAddonsList()

  const amount = addonsList
    .map(({price}) => parseFloat(String(price).replace(/[^0-9.-]/g, '')) || 0)
    .reduce((acc, a) => acc + a, 0)

  return (
    <>
      <ParagraphText variant="reg" className="m-0 mt-2 font-weight-bold">
        {paymentState.paymentInfo.plan.planUnit === 'semi-annually'
          ? 'Semi-Annually '
          : 'Monthly '}
        Total
      </ParagraphText>
      <DropdownProducts
        addonsList={addonsList}
        title={`${formatPrice(amount)}`}
      />
    </>
  )
}

const OldFlowDesktop = ({
  currentStep,
  paymentState,
  isLoading,
  todayItems,
  handleChangePaymentMethod,
  paymentMethods,
  calculateTaxes,
  calculatingTaxes,
  setCalculatingTaxes,
  setName,
  setIsAddressUpdated,
  uuid,
  couponDiscountAmount,
  totalNoTaxes,
  calculatedTaxes,
  isUpdatePaymentButtonDisabled,
  handleCompleteOrder,
  name,
  isAddressUpdated,
  setAddressInformation,
  addressInformation,
  updateParentAddress,
  paymentRequestState,
  setPaymentRequestState,
  isProcessPurchaseComplete,
  linkExpiryInMilliSec,
}) => {
  const {checkBoxesValues, setCheckBoxesValues, isAgreeCheckboxDisabled} =
    usePaymentCheckboxes()
  const {addressInformation: contextAddressInformation} = usePaymentAddress()
  const hasSupportFee =
    paymentState.monthlyAddons.some(({code}) => code === '247support-m2m') ||
    todayItems.some(({code}) => code === '247support-m2m')
  const supportFee =
    paymentState.monthlyAddons.find(({code}) => code === '247support-m2m') ||
    todayItems.find(({code}) => code === '247support-m2m')
  const isAus = paymentState.paymentInfo?.country === 'AU'
  const isAusNZ = ['AU', 'NZ'].includes(paymentState.paymentInfo?.country)
  const isInvoiceBilled = paymentState.paymentInfo?.isInvoiceBilled
  const [isBillingNoticeDisplayed, setIsBillingNoticeDisplayed] =
    React.useState(false)
  console.log(
    'isBillingNoticeDisplayed from old-flow-desktop:: ',
    isBillingNoticeDisplayed,
  )
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={currentStep === 2 ? {height: '90vh'} : {}}
    >
      <div>
        <BillingNoticeModal
          setIsBillingNoticeDisplayed={setIsBillingNoticeDisplayed}
          calledFromPaymentLink={true}
        />
      </div>
      <MainContainer className={`p-4 ${isAus && currentStep === 1 && 'pb-5'}`}>
        {isLoading ? (
          <SkeletonLoader width="100%" height="80vh" />
        ) : (
          <>
            {currentStep === 1 && (
              <>
                <div className="d-flex justify-content-between mb-4">
                  <img src={thryvSalesLogo} alt="Thryv Sales Logo" />
                </div>
                <ParagraphText variant="lg" className="mt-2">
                  Enter your payment information to finish processing your Thryv
                  order.
                </ParagraphText>
                <TotalOrSemiAnnualDue paymentState={paymentState} />
                <ParagraphText
                  variant="lg"
                  className="m-0 mt-2 mb-5 font-weight-bold total-price"
                ></ParagraphText>
                <ExpiryCountdownTimer
                  countdownTimeMS={linkExpiryInMilliSec}
                  isHourlyNeeded={linkExpiryInMilliSec > 60 * 60 * 1000}
                />
                {(['US', 'AU'].includes(paymentState.paymentInfo.country) ||
                  isAusNZ) &&
                  (!paymentState?.paymentInfo?.isInvoiceBilled || isAusNZ) && (
                    <>
                      <ParagraphText
                        variant="lg"
                        className="font-weight-bold mt-4"
                      >
                        Select Payment Method
                      </ParagraphText>
                      <PaymentMethod
                        paymentMethod={paymentState.paymentMethod}
                        handleChangePaymentMethod={handleChangePaymentMethod}
                        paymentMethods={paymentMethods}
                        isDesktop={true}
                      />
                    </>
                  )}
                {!!paymentState?.paymentInfo?.isInvoiceBilled &&
                  (!isAusNZ ||
                    paymentState?.paymentMethod?.value === 'invoice') && (
                    <InvoicePaymentForm
                      calculateTaxes={calculateTaxes}
                      calculatingTaxes={calculatingTaxes}
                      setCalculatingTaxes={setCalculatingTaxes}
                      businessAddress={
                        paymentState.paymentInfo?.businessAddress
                      }
                      country={paymentState?.paymentInfo?.country}
                      setName={setName}
                      handleCompleteOrder={handleCompleteOrder}
                      setIsAddressUpdated={setIsAddressUpdated}
                      bizphone={
                        paymentState.paymentInfo?.businessAddress?.phone
                      }
                      is5Order={false}
                      isAddressUpdated={isAddressUpdated}
                      uuid={uuid}
                      addressInformation={addressInformation}
                      setAddressInformation={setAddressInformation}
                      updateParentAddress={updateParentAddress}
                    />
                  )}
                {paymentState?.paymentMethod?.value === 'card' &&
                  (!isInvoiceBilled || isAusNZ) && (
                    <PaymentForm
                      calculateTaxes={calculateTaxes}
                      calculatingTaxes={calculatingTaxes}
                      setCalculatingTaxes={setCalculatingTaxes}
                      businessAddress={
                        paymentState?.paymentInfo?.businessAddress
                      }
                      country={paymentState?.paymentInfo?.country}
                      setName={setName}
                      handleCompleteOrder={handleCompleteOrder}
                      setIsAddressUpdated={setIsAddressUpdated}
                      bizphone={
                        paymentState.paymentInfo?.businessAddress?.phone
                      }
                      paymentRequestState={paymentRequestState}
                      setPaymentRequestState={setPaymentRequestState}
                    />
                  )}
                {paymentState?.paymentMethod?.value === 'ach' &&
                  (!isInvoiceBilled || isAusNZ) && (
                    <AchPaymentForm
                      calculateTaxes={calculateTaxes}
                      calculatingTaxes={calculatingTaxes}
                      setCalculatingTaxes={setCalculatingTaxes}
                      businessAddress={
                        paymentState?.paymentInfo?.businessAddress
                      }
                      country={paymentState?.paymentInfo?.country}
                      setName={setName}
                      handleCompleteOrder={handleCompleteOrder}
                      setIsAddressUpdated={setIsAddressUpdated}
                      bizphone={
                        paymentState.paymentInfo?.businessAddress?.phone
                      }
                    />
                  )}
                {paymentState?.paymentMethod?.value === 'becs' && (
                  <BecsPaymentForm
                    calculateTaxes={calculateTaxes}
                    calculatingTaxes={calculatingTaxes}
                    setCalculatingTaxes={setCalculatingTaxes}
                    businessAddress={paymentState?.paymentInfo?.businessAddress}
                    country={paymentState?.paymentInfo?.country}
                    setName={setName}
                    handleCompleteOrder={handleCompleteOrder}
                    setIsAddressUpdated={setIsAddressUpdated}
                    bizphone={paymentState.paymentInfo?.businessAddress?.phone}
                  />
                )}
                <hr />
                {Boolean(
                  paymentState?.paymentInfo?.balanceInfo?.availableCreditAmount,
                ) && (
                  <>
                    <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-2">
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        Credit
                      </ParagraphText>
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        {formatPrice(
                          paymentState?.paymentInfo?.balanceInfo
                            ?.availableCreditAmount || 0,
                        )}
                      </ParagraphText>
                    </div>
                    <hr />
                  </>
                )}
                {paymentState.monthlyAmount > 0 && (
                  <>
                    <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-2">
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        Monthly Total
                      </ParagraphText>
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        {formatPrice(paymentState.monthlyAmount)}
                      </ParagraphText>
                    </div>
                    {
                      <div>
                        <ParagraphText
                          variant="sm"
                          className="font-weight-bold p-0 pr-4 pl-4"
                          style={{marginTop: '-16px'}}
                        >
                          {isAus
                            ? '*Excludes fees and incentives'
                            : '*Excludes taxes, fees, and incentives'}
                        </ParagraphText>
                      </div>
                    }
                    <hr />
                  </>
                )}
                {paymentState.semiAnnuallyAmount > 0 && (
                  <>
                    <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-2">
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        Semi-Annually Total
                      </ParagraphText>
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        $ {paymentState.semiAnnuallyAmount}
                      </ParagraphText>
                    </div>
                    <hr />
                  </>
                )}
                <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-4">
                  <ParagraphText
                    variant="lg"
                    color="thryv-black-500"
                    className="font-weight-bold"
                  >
                    Sub Total (
                    {isInvoiceBilled ? 'Due Next Invoice' : 'Due Today'})
                  </ParagraphText>
                  <ParagraphText
                    variant="lg"
                    color="thryv-black-500"
                    className="font-weight-bold"
                  >
                    ${' '}
                    {paymentState?.calculatedTaxes && !isAus
                      ? parseFloat(
                          paymentState.totalPaymentAmountWithoutTax ?? 0,
                        ).toFixed(2)
                      : paymentState?.calculatedTaxes && isAus
                      ? parseFloat(paymentState.totalPaymentAmount).toFixed(2)
                      : paymentState?.paymentInfo?.paymentPreview?.total != null
                      ? parseFloat(
                          paymentState.paymentInfo.paymentPreview.total,
                        ).toFixed(2)
                      : Math.max(
                          paymentState.totalPaymentAmountNoTaxes -
                            couponDiscountAmount +
                            (paymentState?.paymentInfo?.balanceInfo
                              ?.availableCreditAmount || 0),
                          0,
                        ).toFixed(2)}
                  </ParagraphText>
                </div>
                <hr />
                {!isAus && (
                  <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-2">
                    <ParagraphText
                      variant="lg"
                      color="thryv-black-500"
                      className="font-weight-bolder"
                    >
                      {paymentState.calculatedTaxes != null
                        ? 'Tax'
                        : 'Tax (to be calculated)'}
                    </ParagraphText>
                    <ParagraphText
                      variant="lg"
                      color="thryv-black-500"
                      className="font-weight-bolder"
                    >
                      {paymentState.calculatedTaxes != null
                        ? `$ ${Number(paymentState.calculatedTaxes).toFixed(2)}`
                        : '-'}
                    </ParagraphText>
                  </div>
                )}
                {!isAus && <hr />}
                {hasSupportFee && (
                  <>
                    <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-2">
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >
                        24x7 Service & Support Fee
                      </ParagraphText>
                      <ParagraphText
                        variant="lg"
                        color="thryv-black-500"
                        className="font-weight-bolder"
                      >{`$ ${supportFee.currency.unitAmount.toFixed(
                        2,
                      )}`}</ParagraphText>
                    </div>
                    {isAus && <hr />}
                  </>
                )}
                <div className="d-flex justify-content-between p-0 pr-4 pl-4 m-0 mt-2 mb-2">
                  <div
                    className="d-flex align-items-center"
                    style={{gap: '6px'}}
                  >
                    <ParagraphText
                      variant="lg"
                      color="thryv-black-500"
                      className="font-weight-bold"
                    >
                      Total (
                      {isInvoiceBilled ? 'Due Next Invoice' : 'Due Today'})
                    </ParagraphText>
                    {!isInvoiceBilled && (
                      <Tooltip
                        variant="top"
                        description="The amount shown as Total Due Today may vary slightly between checkout and the payment link. This is due to real-time proration based on your product selections and billing period, ensuring precise billing."
                        width="320px"
                      >
                        <Icon
                          type="regular"
                          variant="infoCircle"
                          color="#FF5000"
                          width="17"
                          height="17"
                        />
                      </Tooltip>
                    )}
                  </div>
                  <ParagraphText
                    variant="lg"
                    color="thryv-black-500"
                    className="font-weight-bold"
                  >
                    ${' '}
                    {paymentState.calculatedTaxes
                      ? paymentState?.totalPaymentAmount != null
                        ? parseFloat(paymentState.totalPaymentAmount).toFixed(2)
                        : Math.max(
                            !isAus
                              ? paymentState?.paymentInfo?.paymentPreview
                                  ?.total != null
                                ? paymentState?.paymentInfo?.paymentPreview
                                    ?.total +
                                  paymentState?.paymentInfo?.paymentPreview?.tax
                                : Number(totalNoTaxes) +
                                  calculatedTaxes -
                                  couponDiscountAmount
                              : paymentState?.paymentInfo?.paymentPreview
                                  ?.total != null
                              ? paymentState?.paymentInfo?.paymentPreview?.total
                              : Number(totalNoTaxes) - couponDiscountAmount,
                            0,
                          ).toFixed(2)
                      : Math.max(
                          paymentState?.paymentInfo?.paymentPreview?.total !=
                            null
                            ? paymentState?.paymentInfo?.paymentPreview?.total
                            : Number(totalNoTaxes) - couponDiscountAmount,
                          0,
                        ).toFixed(2)}{' '}
                    {paymentState.calculatedTaxes == null &&
                      !isAus &&
                      ' + taxes'}
                  </ParagraphText>
                </div>
                <FutureInvoicesNotice />
                <div>
                  {isAus && (
                    <div>
                      <ParagraphText
                        variant="sm"
                        className="font-weight-bold mt-4"
                      >
                        {'*All plans include GST'}
                      </ParagraphText>
                    </div>
                  )}
                </div>

                <div className="mt-4 mb-4">
                  <ParagraphText variant="sm" style={{fontSize: '14px'}}>
                    *This initial Subscription Period is 6 months. Please note,
                    certain Thryv Services contain an additional per user fee
                    which will vary based on the number of applicable
                    users/employees/contractors.
                  </ParagraphText>
                  <br></br>
                  <Checkbox
                    hasLabel
                    name="terms"
                    title={
                      isAgreeCheckboxDisabled
                        ? 'Please complete your payment information.'
                        : undefined
                    }
                    checked={checkBoxesValues.agreedTerms}
                    disabled={isAgreeCheckboxDisabled}
                    onChange={e => {
                      setCheckBoxesValues({
                        ...checkBoxesValues,
                        agreedTerms: e.currentTarget.checked,
                      })
                    }}
                    label={
                      <label
                        style={{
                          marginTop: '5px',
                        }}
                      >
                        By checking this box, you agree to our{' '}
                        <a
                          target="_blank"
                          href="https://corporate.thryv.com/terms/"
                          rel="noreferrer"
                        >
                          Terms & Conditions
                        </a>
                      </label>
                    }
                  />
                  <br></br>
                  <ParagraphText variant="sm">
                    By processing your payment, you agree that your personal
                    information will be shared with Thryv’s payment
                    processor—Adyen, N.V. and will be subject to Adyen’s{' '}
                    <a
                      target="_blank"
                      href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
                      rel="noreferrer"
                    >
                      privacy statement
                    </a>
                    .
                  </ParagraphText>
                </div>
                {paymentState.paymentInfo.isInvoiceBilled &&
                  (!isAusNZ ||
                    paymentState?.paymentMethod?.value === 'invoice') && (
                    <div className="m-3 d-flex justify-content-center">
                      <Button
                        variant="primary"
                        type="button"
                        disabled={
                          calculatingTaxes || !checkBoxesValues.agreedTerms
                        }
                        onClick={() => {
                          updateParentAddress(
                            contextAddressInformation,
                            paymentState.paymentInfo.businessAddress.phone,
                            handleCompleteOrder,
                          )
                        }}
                      >
                        Complete Purchase
                      </Button>
                    </div>
                  )}
                {!paymentState.paymentInfo.isInvoiceBilled && (
                  <div className="m-3 d-flex justify-content-center">
                    <Button
                      variant="primary"
                      type="button"
                      style={
                        !checkBoxesValues.agreedTerms
                          ? {backgroundColor: '#999'}
                          : {}
                      }
                      onClick={() => {
                        if (!checkBoxesValues.agreedTerms) {
                          toast.warn(
                            'Please agree to the Terms and Conditions to continue.',
                          )
                          return null
                        }
                        handleCompleteOrder()
                      }}
                    >
                      Complete Purchase
                    </Button>
                  </div>
                )}
                {isAusNZ &&
                  paymentState.paymentInfo.isInvoiceBilled &&
                  paymentState?.paymentMethod?.value !== 'invoice' && (
                    <div className="m-3 d-flex justify-content-center">
                      <Button
                        variant="primary"
                        type="button"
                        style={
                          !checkBoxesValues.agreedTerms
                            ? {backgroundColor: '#999'}
                            : {}
                        }
                        onClick={() => {
                          if (!checkBoxesValues.agreedTerms) {
                            toast.warn(
                              'Please agree to the Terms and Conditions to continue.',
                            )
                            return null
                          }
                          handleCompleteOrder()
                        }}
                      >
                        Complete Purchase
                      </Button>
                    </div>
                  )}
              </>
            )}
            {currentStep === 2 &&
              !calculatingTaxes &&
              isProcessPurchaseComplete && (
                <div className="d-flex flex-column align-items-center">
                  <Icon
                    style={{marginBottom: '25px'}}
                    type="solid"
                    variant="circleCheck"
                    color="#5cb95c"
                    width="48px"
                    height="48px"
                  />
                  <ParagraphText variant="lg">
                    <strong>
                      Thank you {name}, your payment information is successfully
                      updated!
                    </strong>
                  </ParagraphText>
                  <ParagraphText variant="lg">
                    We will use this information to bill your Thryv
                    subscriptions each month.
                  </ParagraphText>
                  <ParagraphText variant="lg">
                    You can update your billing information through the My
                    Account page.
                  </ParagraphText>
                  <ParagraphText variant="lg">
                    <strong>Thank you for purchasing Thryv!</strong>
                  </ParagraphText>
                </div>
              )}
          </>
        )}
      </MainContainer>
    </div>
  )
}

export default OldFlowDesktop
